import { useCallback, useEffect, useState } from "react";
import { HCaptchaField } from "./HCaptchaField";

const GATE_KEY = "tnk_homepage_gate";
export const HOMEPAGE_CAPTCHA_RELOCK_EVENT = "tnk-homepage-captcha-relock";
export const HOMEPAGE_CAPTCHA_SUCCESS_EVENT = "tnk-homepage-captcha-success";

/** Canonical message from backend when captcha success is missing. */
export const CAPTCHA_FAILED_COMPLETE_MSG =
  "Captcha verification failed. Please complete the captcha.";

type CaptchaErrorPayload = {
  captcha_ok?: boolean;
  captcha_required?: boolean;
  message?: string;
  error?: string;
  status?: string;
};

/**
 * True when the API (or thrown Error) indicates captcha was not successfully verified.
 * Always re-show the homepage captcha modal when this returns true.
 */
export function isCaptchaVerificationError(
  message: string,
  data?: CaptchaErrorPayload | null,
): boolean {
  if (data && data.captcha_ok === false) return true;
  if (data && data.captcha_required === true) {
    const blob = `${data.message || ""} ${data.error || ""} ${message || ""}`.toLowerCase();
    if (blob.includes("captcha")) return true;
  }
  const m = (message || "").toLowerCase();
  return (
    m.includes("captcha verification failed") ||
    m.includes("complete the captcha") ||
    m.includes("complete the check") ||
    (m.includes("captcha") &&
      (m.includes("failed") || m.includes("required") || m.includes("complete")))
  );
}

/** Drop a stale captcha-failure banner after the homepage check succeeds. */
export function clearErrorAfterCaptchaSuccess(current: string | null): string | null {
  if (current && isCaptchaVerificationError(current)) return null;
  return current;
}

type Props = {
  children: React.ReactNode;
};

/**
 * When HOMEPAGE_CAPTCHA_REQUIRED is on: blur the landing page and show a
 * non-dismissible captcha modal until hCaptcha succeeds. Then issue a gate
 * token for subsequent train/analyze/ingest calls.
 */
export function HomepageCaptchaGate({ children }: Props) {
  const [checking, setChecking] = useState(true);
  const [required, setRequired] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaReset, setCaptchaReset] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);

  const relock = useCallback(() => {
    clearHomepageGateToken();
    setUnlocked(false);
    setCaptchaToken("");
    setCaptchaReset((n) => n + 1);
    setError(CAPTCHA_FAILED_COMPLETE_MSG);
  }, []);

  useEffect(() => {
    const onRelock = () => relock();
    window.addEventListener(HOMEPAGE_CAPTCHA_RELOCK_EVENT, onRelock);
    return () => window.removeEventListener(HOMEPAGE_CAPTCHA_RELOCK_EVENT, onRelock);
  }, [relock]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/customers/auth-config?purpose=homepage", {
          headers: { Accept: "application/json" },
        });
        const data = (await res.json()) as {
          hcaptcha_enabled?: boolean;
          captcha_required?: boolean;
          captcha_config_error?: string | null;
        };
        if (cancelled) return;

        const need = Boolean(data.captcha_required);
        setRequired(need);
        setConfigError(
          need && !data.hcaptcha_enabled
            ? data.captcha_config_error ||
                "Captcha is required but not configured. Set HCAPTCHA_SITE_KEY and HCAPTCHA_SECRET_KEY."
            : null,
        );

        if (!need) {
          setUnlocked(true);
          return;
        }

        // Required but widget cannot load — keep locked and show config error.
        if (!data.hcaptcha_enabled) {
          clearHomepageGateToken();
          setUnlocked(false);
          return;
        }

        const existing = sessionStorage.getItem(GATE_KEY);
        if (!existing) {
          setUnlocked(false);
          return;
        }

        // Validate stored gate (expired / SECRET_KEY change / stale after deploy).
        const check = await fetch("/api/train/homepage-gate", {
          headers: {
            Accept: "application/json",
            "X-Homepage-Gate": existing,
          },
        });
        const gateStatus = (await check.json().catch(() => ({}))) as {
          valid?: boolean;
        };
        if (cancelled) return;
        if (gateStatus.valid) {
          setUnlocked(true);
        } else {
          clearHomepageGateToken();
          setUnlocked(false);
        }
      } catch {
        if (!cancelled) {
          // Fail closed when we cannot confirm captcha is optional.
          setRequired(true);
          setUnlocked(false);
          setConfigError(
            "Could not verify captcha status. Refresh the page, then complete the captcha.",
          );
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!required || unlocked) {
      document.body.classList.remove("homepage-captcha-locked");
      return;
    }
    document.body.classList.add("homepage-captcha-locked");
    return () => document.body.classList.remove("homepage-captcha-locked");
  }, [required, unlocked]);

  const submitToken = useCallback(async (token: string) => {
    if (!token) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch("/api/train/homepage-captcha", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ h_captcha_response: token }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        success?: boolean;
        captcha_ok?: boolean;
        captcha_required?: boolean;
        gate_token?: string;
        error?: string;
        message?: string;
      };
      if (!res.ok || !data.success || data.captcha_ok === false) {
        throw new Error(data.error || data.message || CAPTCHA_FAILED_COMPLETE_MSG);
      }
      const gate = data.gate_token || "";
      // When captcha is required, a gate token must exist after success.
      if (!gate && data.captcha_required !== false) {
        throw new Error(CAPTCHA_FAILED_COMPLETE_MSG);
      }
      if (gate) sessionStorage.setItem(GATE_KEY, gate);
      else sessionStorage.removeItem(GATE_KEY);
      setUnlocked(true);
      setCaptchaToken("");
      setError(null);
      window.dispatchEvent(new Event(HOMEPAGE_CAPTCHA_SUCCESS_EVENT));
    } catch (err) {
      setError(err instanceof Error ? err.message : CAPTCHA_FAILED_COMPLETE_MSG);
      setCaptchaToken("");
      setCaptchaReset((n) => n + 1);
      setUnlocked(false);
      clearHomepageGateToken();
    } finally {
      setSubmitting(false);
    }
  }, []);

  useEffect(() => {
    if (!captchaToken || submitting) return;
    void submitToken(captchaToken);
  }, [captchaToken, submitToken, submitting]);

  const showModal = required && !unlocked && !checking;

  return (
    <>
      <div className={showModal ? "homepage-captcha-content--blurred" : undefined}>
        {children}
      </div>
      {showModal ? (
        <div
          className="homepage-captcha-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="homepage-captcha-title"
        >
          <div className="homepage-captcha-modal">
            <h2 id="homepage-captcha-title" className="homepage-captcha-title">
              Confirm you are human
            </h2>
            <p className="homepage-captcha-lead">
              Complete the check below to continue. This window stays open until
              verification succeeds.
            </p>
            {configError ? (
              <p className="model-run-error" role="alert">
                {configError}
              </p>
            ) : (
              <div className="homepage-captcha-widget">
                <HCaptchaField
                  purpose="homepage"
                  onToken={setCaptchaToken}
                  resetKey={captchaReset}
                />
              </div>
            )}
            {submitting ? <p className="muted">Verifying…</p> : null}
            {error ? (
              <p className="model-run-error" role="alert">
                {error}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
}

/** Read gate token for train/analyze FormData (empty if not required / not set). */
export function readHomepageGateToken(): string {
  try {
    return sessionStorage.getItem(GATE_KEY) || "";
  } catch {
    return "";
  }
}

export function clearHomepageGateToken(): void {
  try {
    sessionStorage.removeItem(GATE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Clear gate and re-show homepage captcha modal.
 * Call whenever API returns captcha failure / captcha_ok: false.
 */
export function requestHomepageCaptchaRelock(): void {
  clearHomepageGateToken();
  window.dispatchEvent(new Event(HOMEPAGE_CAPTCHA_RELOCK_EVENT));
}

/**
 * Confirm a successful homepage gate exists before upload.
 * Returns false and re-shows captcha when success is missing.
 */
export async function ensureHomepageCaptchaSuccess(): Promise<{
  ok: boolean;
  gate: string;
  message?: string;
}> {
  try {
    const cfgRes = await fetch("/api/customers/auth-config?purpose=homepage", {
      headers: { Accept: "application/json" },
    });
    const cfg = (await cfgRes.json().catch(() => ({}))) as {
      captcha_required?: boolean;
      hcaptcha_enabled?: boolean;
    };
    if (!cfg.captcha_required) {
      return { ok: true, gate: "" };
    }
    const gate = readHomepageGateToken();
    if (!gate) {
      requestHomepageCaptchaRelock();
      return { ok: false, gate: "", message: CAPTCHA_FAILED_COMPLETE_MSG };
    }
    const check = await fetch("/api/train/homepage-gate", {
      headers: {
        Accept: "application/json",
        "X-Homepage-Gate": gate,
      },
    });
    const gateStatus = (await check.json().catch(() => ({}))) as { valid?: boolean };
    if (!gateStatus.valid) {
      requestHomepageCaptchaRelock();
      return { ok: false, gate: "", message: CAPTCHA_FAILED_COMPLETE_MSG };
    }
    return { ok: true, gate };
  } catch {
    requestHomepageCaptchaRelock();
    return { ok: false, gate: "", message: CAPTCHA_FAILED_COMPLETE_MSG };
  }
}
